if (!customElements.get('variant-configurator')) {
  class VariantConfigurator extends HTMLElement {
    constructor() {
      super();

      this.optionKeys = ['lens_type', 'lens_color', 'frame_color'];
      this.optionPositions = {
        lens_type: 1,
        lens_color: 2,
        frame_color: 3,
      };
      this.optionNames = {
        lens_type: 'Lens Type',
        lens_color: 'Lens Color',
        frame_color: 'Frame Color',
      };
      this.selected = {};
      this.currentVariant = null;
    }

    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.productHandle = String(this.productUrl || '').split('/').filter(Boolean).pop() || '';
      this.initialVariantId = Number(this.dataset.initialVariantId || 0);
      this.optionPositions = {
        ...this.optionPositions,
        ...this.parseJson('option-position-map', {}),
      };
      this.nativeVariantsById = this.indexVariantsById(this.parseJson('native-variants', []));
      this.variantGalleryFiles = this.buildVariantGalleryIndex(this.parseJson('variant-gallery-files', {}));
      this.productMediaIndex = this.buildProductMediaIndex(this.parseJson('product-media-index', []));
      const defaultLensColorContent = this.parseJson('lens-color-content', {});
      const lensColorContentOverrides = this.parseJson('lens-color-content-variant-overrides', {});
      this.lensColorContent = this.mergeLensColorContent(defaultLensColorContent, lensColorContentOverrides);
      this.swatchMap = this.mergeSwatchMaps(
        this.buildSwatchMap(this.parseJson('swatch-entries', [])),
        this.buildSwatchMapFromValueImageMap(this.parseJson('swatch-value-image-map', {})),
      );
      this.groupNodes = {
        lens_type: this.querySelector('[data-option-group="lens_type"]'),
        lens_color: this.querySelector('[data-option-group="lens_color"]'),
        frame_color: this.querySelector('[data-option-group="frame_color"]'),
      };
      this.selectedLabelNodes = {
        lens_type: this.querySelector('[data-selected-label="lens_type"]'),
        lens_color: this.querySelector('[data-selected-label="lens_color"]'),
        frame_color: this.querySelector('[data-selected-label="frame_color"]'),
      };
      this.lensColorSupportTitle = this.querySelector('[data-lens-color-support-title]');
      this.lensColorSupportBody = this.querySelector('[data-lens-color-support-body]');
      this.lensColorSupportBodySecondary = this.querySelector('[data-lens-color-support-body-secondary]');

      this.variantData = this.normalizeVariants(this.parseJson('sellable-variants', []));
      if (!this.variantData.length) return;

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('keydown', this.handleKeydown.bind(this));

      const initialVariant = this.variantData.find((variant) => variant.id === this.initialVariantId)
        || this.variantData[0];

      const waitForGallery = () => {
        const mediaGallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          this.syncUIFromVariant(initialVariant);
        } else {
          Promise.all([
            customElements.whenDefined('media-gallery'),
            customElements.whenDefined('slider-component'),
          ]).then(() => {
            requestAnimationFrame(() => this.syncUIFromVariant(initialVariant));
          });
        }
      };
      waitForGallery();
    }

    parseJson(key, fallback = {}) {
      const node = this.querySelector(`[data-${key}]`);
      if (!node) return fallback;
      try {
        return JSON.parse(node.textContent);
      } catch (_error) {
        return fallback;
      }
    }

    buildSwatchMap(entries) {
      const map = {};
      (entries || []).forEach((entry) => {
        const group = entry?.group;
        const value = entry?.value;
        const image = entry?.image;
        if (!group || !value || !image) return;
        map[group] ||= {};
        map[group][this.normalizeSwatchKey(value)] = image;
      });
      return map;
    }

    buildSwatchMapFromValueImageMap(rawMap) {
      const map = {};
      if (!rawMap || typeof rawMap !== 'object') return map;

      Object.entries(rawMap).forEach(([group, groupEntries]) => {
        if (!groupEntries || typeof groupEntries !== 'object') return;
        Object.entries(groupEntries).forEach(([value, image]) => {
          if (!value || !image) return;
          map[group] ||= {};
          map[group][this.normalizeSwatchKey(value)] = image;
        });
      });

      return map;
    }

    mergeSwatchMaps(primary, fallback) {
      const merged = {};
      const groups = new Set([
        ...Object.keys(primary || {}),
        ...Object.keys(fallback || {}),
      ]);

      groups.forEach((group) => {
        merged[group] = {
          ...(fallback?.[group] || {}),
          ...(primary?.[group] || {}),
        };
      });

      return merged;
    }

    mergeLensColorContent(defaults, overrides) {
      const merged = { ...(defaults || {}) };
      Object.entries(overrides || {}).forEach(([key, value]) => {
        merged[key] = {
          ...(merged[key] || {}),
          ...(value || {}),
        };
      });
      return merged;
    }

    normalizeSwatchKey(value) {
      return String(value || '')
        .toLowerCase()
        .trim()
        // Drop punctuation so "w/Black" and "w/ Black" converge.
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    normalizeVariants(variants) {
      const rows = Array.isArray(variants) ? variants : [];
      return rows.map((variant) => {
        const native = this.nativeVariantsById.get(Number(variant.id)) || {};
        const lensType = variant.lens_type || this.getVariantOptionValue(variant, this.optionPositions.lens_type) || this.getVariantOptionValue(native, this.optionPositions.lens_type);
        const lensColor = variant.lens_color || this.getVariantOptionValue(variant, this.optionPositions.lens_color) || this.getVariantOptionValue(native, this.optionPositions.lens_color);
        const frameColor = variant.frame_color || this.getVariantOptionValue(variant, this.optionPositions.frame_color) || this.getVariantOptionValue(native, this.optionPositions.frame_color);

        return {
          ...native,
          id: variant.id,
          title: variant.title,
          available: Boolean(variant.available),
          price: variant.price ?? native.price,
          sku: variant.sku || native.sku || '',
          option1: this.getVariantOptionValue(native, 1) || this.getVariantOptionValue(variant, 1) || '',
          option2: this.getVariantOptionValue(native, 2) || this.getVariantOptionValue(variant, 2) || '',
          option3: this.getVariantOptionValue(native, 3) || this.getVariantOptionValue(variant, 3) || '',
          lens_type: lensType || '',
          lens_color: lensColor || '',
          frame_color: frameColor || '',
          featured_media_id: variant.featured_media_id || variant.featured_media?.id || native.featured_media?.id || null,
          featured_image_src: native.featured_image?.src || variant.featured_image?.src || '',
        };
      });
    }

    getVariantOptionValue(variant, position) {
      if (!variant || !position) return '';
      const byOption = variant[`option${position}`];
      if (byOption) return byOption;
      const idx = Number(position) - 1;
      return variant.options?.[idx] || '';
    }

    indexVariantsById(variants) {
      const index = new Map();
      variants.forEach((variant) => {
        if (variant?.id) index.set(Number(variant.id), variant);
      });
      return index;
    }

    getOrderedOptionKeys() {
      return [...this.optionKeys];
    }

    matchesSelection(variant, selected) {
      return Object.entries(selected).every(([key, value]) => !value || variant[key] === value);
    }

    getMatchingVariants(variants, selected) {
      return variants.filter((variant) => this.matchesSelection(variant, selected));
    }

    getValidValuesForOption(variants, selected, targetKey) {
      const key = targetKey;
      const values = [];
      const seen = new Set();
      this.getMatchingVariants(variants, selected).forEach((variant) => {
        const value = variant[key];
        if (value && !seen.has(value)) {
          seen.add(value);
          values.push(value);
        }
      });
      return values;
    }

    findExactVariant(variants, selected) {
      return variants.find((variant) => this.optionKeys.every((key) => variant[key] === selected[key])) || null;
    }

    getFallbackVariant(variants, selected, lastChangedKey) {
      const orderedKeys = this.getOrderedOptionKeys();
      const changedIndex = orderedKeys.indexOf(lastChangedKey);
      let bestVariant = null;
      let bestScore = -1;

      variants.forEach((variant) => {
        if (selected[lastChangedKey] && variant[lastChangedKey] !== selected[lastChangedKey]) return;

        let score = 0;
        orderedKeys.forEach((key, index) => {
          if (!selected[key]) return;
          if (variant[key] !== selected[key]) return;

          if (key === lastChangedKey) {
            score += 100;
          } else if (changedIndex !== -1 && index < changedIndex) {
            score += 25 - index;
          } else {
            score += 10 - index;
          }
        });

        if (score > bestScore) {
          bestScore = score;
          bestVariant = variant;
        }
      });

      return bestVariant || variants[0] || null;
    }

    handleClick(event) {
      const button = event.target.closest('[data-option-position][data-option-value]');
      if (!button) return;

      const key = button.dataset.optionKey;
      if (!key) return;
      const nextSelection = { ...this.selected, [key]: button.dataset.optionValue };
      const resolvedVariant = this.findExactVariant(this.variantData, nextSelection)
        || this.getFallbackVariant(this.variantData, nextSelection, key);

      if (resolvedVariant) this.syncUIFromVariant(resolvedVariant);
    }

    handleKeydown(event) {
      const button = event.target.closest('[data-option-position][role="radio"]');
      if (!button) return;

      const group = button.closest('[role="radiogroup"]');
      const buttons = Array.from(group.querySelectorAll('[role="radio"]'));
      const currentIndex = buttons.indexOf(button);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % buttons.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = buttons.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      const nextButton = buttons[nextIndex];
      nextButton.focus();
      nextButton.click();
    }

    renderOptionGroups() {
      const upstreamSelectionsByKey = {
        lens_type: {},
        lens_color: { lens_type: this.selected.lens_type },
        frame_color: {
          lens_type: this.selected.lens_type,
          lens_color: this.selected.lens_color,
        },
      };

      this.optionKeys.forEach((key) => {
        const position = this.optionPositions[key];
        const upstreamSelections = upstreamSelectionsByKey[key] || {};
        const values = this.getValidValuesForOption(this.variantData, upstreamSelections, key);
        const node = this.groupNodes[key];
        if (!node) return;

        node.innerHTML = values.map((value) => this.renderOptionButton(key, position, value)).join('');
      });

    }

    renderOptionButton(key, position, value) {
      const selected = this.selected[key] === value;
      const swatchUrl = this.swatchMap?.[key]?.[this.normalizeSwatchKey(value)] || '';
      const shouldRenderSwatch = (key === 'lens_color' || key === 'frame_color') && Boolean(swatchUrl);
      const classes = [
        'product-configurator__option',
        shouldRenderSwatch ? 'product-configurator__option--swatch' : 'product-configurator__option--text',
        `product-configurator__option--${key.replace('_', '-')}`,
        selected ? 'is-active' : '',
      ].join(' ');
      const swatchMarkup = shouldRenderSwatch
        ? `<span class="product-configurator__swatch-visual"><img src="${swatchUrl}" alt="" loading="lazy" decoding="async"></span><span class="visually-hidden">${value}</span>`
        : `<span class="product-configurator__option-text">${value}</span>`;

      return `
        <button
          type="button"
          class="${classes}"
          role="radio"
          aria-checked="${selected ? 'true' : 'false'}"
          aria-disabled="false"
          tabindex="${selected ? '0' : '-1'}"
          data-option-key="${key}"
          data-option-position="${position}"
          data-option-value="${this.escapeAttribute(value)}"
        >
          ${swatchMarkup}
        </button>
      `;
    }

    syncSelectedLabels(variant) {
      this.selectedLabelNodes.lens_type.textContent = variant.lens_type;
      this.selectedLabelNodes.lens_color.textContent = variant.lens_color;
      this.selectedLabelNodes.frame_color.textContent = variant.frame_color;
    }

    syncLensColorSupportText(variant) {
      const content = this.lensColorContent[variant.lens_color] || {
        title: variant.lens_color,
        body: '',
        secondary_body: '',
      };
      const selectedLensColor = String(variant.lens_color || '').trim();
      const title = String(content.title || variant.lens_color || '').trim();
      const body = String(content.body || '').trim();
      const secondaryBody = String(content.secondary_body || '').trim();
      const hideTitle = !title || title.toLowerCase() === selectedLensColor.toLowerCase();

      if (this.lensColorSupportTitle) {
        this.lensColorSupportTitle.textContent = title;
        this.lensColorSupportTitle.hidden = hideTitle;
      }
      if (this.lensColorSupportBody) {
        this.lensColorSupportBody.textContent = body;
      }
      if (this.lensColorSupportBodySecondary) {
        this.lensColorSupportBodySecondary.textContent = secondaryBody;
        this.lensColorSupportBodySecondary.hidden = !secondaryBody;
      }
    }

    syncUIFromVariant(variant) {
      if (!variant) return;

      this.currentVariant = variant;
      this.selected = {
        lens_type: variant.lens_type,
        lens_color: variant.lens_color,
        frame_color: variant.frame_color,
      };

      this.renderOptionGroups();
      this.syncSelectedLabels(variant);
      this.syncLensColorSupportText(variant);
      this.removeErrorMessage();
      this.setActiveMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
      this.updatePickupAvailability();
      this.toggleAddButton(!this.currentVariant.available);
    }

    setActiveMedia() {
      const mediaGallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      if (!mediaGallery) return;
      if (typeof mediaGallery.setActiveMedia !== 'function') return;

      const variantId = String(this.currentVariant?.id || '');
      const featuredMediaId = Number(this.currentVariant?.featured_media_id || 0);
      const galleryFilenames = this.variantGalleryFiles?.[variantId] || [];

      const mediaIds = new Set();
      if (featuredMediaId > 0) mediaIds.add(featuredMediaId);
      galleryFilenames.forEach((filename) => {
        const id = this.productMediaIndex?.get(filename);
        if (id) mediaIds.add(id);
      });

      if (!mediaIds.size) {
        // No data mapped to this variant — reveal everything so the gallery isn't left stale
        mediaGallery.querySelectorAll('[data-media-id]').forEach((node) => {
          node.classList.remove('variant-configurator__media-hidden');
        });
        mediaGallery.querySelectorAll('[data-target]').forEach((node) => {
          node.classList.remove('variant-configurator__media-hidden', 'variant-configurator__media-featured');
        });
        const productModalFallback = document.getElementById(`ProductModal-${this.sectionId}`);
        if (productModalFallback) {
          productModalFallback.querySelectorAll('[data-media-id]').forEach((node) => {
            node.classList.remove('variant-configurator__media-hidden');
          });
        }
        return;
      }

      // Build the ordered list of media IDs as they should appear in the variant's gallery:
      // featured first, then the gallery URLs in their specified order.
      const orderedMediaIds = [];
      if (featuredMediaId > 0) orderedMediaIds.push(featuredMediaId);
      galleryFilenames.forEach((filename) => {
        const id = this.productMediaIndex?.get(filename);
        if (id && !orderedMediaIds.includes(id)) orderedMediaIds.push(id);
      });

      // Hide/show main slider items (data-media-id)
      mediaGallery.querySelectorAll('[data-media-id]').forEach((node) => {
        const id = this.parseMediaId(node.getAttribute('data-media-id'));
        node.classList.toggle('variant-configurator__media-hidden', !mediaIds.has(id));
      });

      // Re-order visible slider items to match the variant's gallery order.
      // appendChild on an existing element moves it to the end of its parent,
      // so iterating in order leaves everything correctly positioned.
      const sliderItems = mediaGallery.querySelectorAll('[data-media-id]');
      if (sliderItems.length) {
        const parent = sliderItems[0].parentElement;
        if (parent) {
          orderedMediaIds.forEach((id) => {
            const node = parent.querySelector(`[data-media-id$="-${id}"]`);
            if (node) parent.appendChild(node);
          });
        }
      }

      // Hide/show thumbnail strip items (data-target) and move cover to front
      mediaGallery.querySelectorAll('[data-target]').forEach((node) => {
        const id = this.parseMediaId(node.getAttribute('data-target'));
        const hidden = !mediaIds.has(id);
        node.classList.toggle('variant-configurator__media-hidden', hidden);
        node.classList.toggle('variant-configurator__media-featured', !hidden && id === featuredMediaId);
      });

      // Re-order thumbnail strip items to match the variant's gallery order.
      const thumbItems = mediaGallery.querySelectorAll('[data-target]');
      if (thumbItems.length) {
        const thumbParent = thumbItems[0].parentElement;
        if (thumbParent) {
          orderedMediaIds.forEach((id) => {
            const node = thumbParent.querySelector(`[data-target$="-${id}"]`);
            if (node) thumbParent.appendChild(node);
          });
        }
      }

      if (featuredMediaId > 0) {
        const targetId = `${this.sectionId}-${featuredMediaId}`;
        const activeMedia = mediaGallery.querySelector(`[data-media-id="${targetId}"]`);
        const originalScroll = activeMedia && activeMedia.scrollIntoView;
        if (activeMedia) activeMedia.scrollIntoView = () => {};
        mediaGallery.setActiveMedia(targetId, true);
        if (activeMedia) {
          setTimeout(() => { activeMedia.scrollIntoView = originalScroll; }, 50);
        }
      }

      // Mirror hidden state and order into the product modal's independent media copy
      const productModal = document.getElementById(`ProductModal-${this.sectionId}`);
      if (productModal) {
        const modalItems = productModal.querySelectorAll('[data-media-id]');
        modalItems.forEach((node) => {
          const id = this.parseMediaId(node.getAttribute('data-media-id'));
          node.classList.toggle('variant-configurator__media-hidden', !mediaIds.has(id));
        });
        // Re-order modal items to match gallery order
        if (modalItems.length) {
          const modalParent = modalItems[0].parentElement;
          if (modalParent) {
            orderedMediaIds.forEach((id) => {
              const node = modalParent.querySelector(`[data-media-id="${id}"]`);
              if (node) modalParent.appendChild(node);
            });
          }
        }
      }
    }

    buildVariantGalleryIndex(rawMap) {
      const index = {};
      Object.entries(rawMap || {}).forEach(([variantId, urls]) => {
        const filenames = (Array.isArray(urls) ? urls : [])
          .map((url) => String(url || '').split('?')[0].split('/').pop().toLowerCase())
          .filter(Boolean);
        if (filenames.length) index[String(variantId)] = filenames;
      });
      return index;
    }

    buildProductMediaIndex(rows) {
      const index = new Map();
      (Array.isArray(rows) ? rows : []).forEach((row) => {
        const id = Number(row?.id);
        const filename = String(row?.src || '').split('?')[0].split('/').pop().toLowerCase();
        if (id > 0 && filename && !index.has(filename)) index.set(filename, id);
      });
      return index;
    }

    parseMediaId(value) {
      const match = String(value || '').match(/(\d+)(?!.*\d)/);
      return match ? Number(match[1]) : 0;
    }

    updateURL() {
      if (!this.currentVariant) return;
      const variantUrl = `${this.productUrl}?variant=${this.currentVariant.id}`;
      window.history.replaceState({}, '', variantUrl);
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute('href', `${window.location.origin}${variantUrl}`);
    }

    updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.sectionId}`);
      if (!shareButton || !shareButton.updateUrl) return;
      shareButton.updateUrl(`${window.shopUrl}${this.productUrl}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.sectionId}, #product-form-installment-${this.sectionId}`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('pickup-availability');
      if (!pickUpAvailability) return;

      const canFetchAvailability = typeof pickUpAvailability.fetchAvailability === 'function';
      if (canFetchAvailability && this.currentVariant?.id) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;
      const productForm = section.querySelector('product-form');
      if (productForm) productForm.handleErrorMessage();
    }

    renderProductInfo() {
      fetch(`${this.productUrl}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const priceDestination = document.getElementById(`price-${this.sectionId}`);
          const priceSource = html.getElementById(`price-${this.sectionId}`);
          if (priceSource && priceDestination) priceDestination.innerHTML = priceSource.innerHTML;

          const skuDestination = document.getElementById(`Sku-${this.sectionId}`);
          const skuSource = html.getElementById(`Sku-${this.sectionId}`);
          if (skuSource && skuDestination) {
            skuDestination.innerHTML = skuSource.innerHTML;
            skuDestination.className = skuSource.className;
          }

          if (priceDestination) priceDestination.classList.remove('visibility-hidden');
        });
    }

    toggleAddButton(disable = true, text) {
      const productForm = document.getElementById(`product-form-${this.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span');
      if (!addButton || !addButtonText) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        addButtonText.textContent = text || window.variantStrings.soldOut;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.textContent = window.variantStrings.addToCart;
      }
    }

    escapeAttribute(value) {
      return value
        .replaceAll('&', '&amp;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
    }
  }

  customElements.define('variant-configurator', VariantConfigurator);
}
/* cache-bust 1777654934 */
